<template>
  <div v-if="accounts.length > 0" class="reporting-filter-wrapper">
    <div class="reporting-filter-wrapper__filters">
      <div class="row">
        <!-- TrafficSource Types -->
        <div class="col-md-3">
          <vue-opti-select-light
            class="optimizer-select"
            :options="$c_trafficSourceTypeOptions"
            unique-key="uniqueName"
            label-key="name"
            :value="models.trafficSourceType"
            @change="$_changeTrafficSourceType"
            button-placeholder="Select Traffic Source"
            button-block
            single
          >
            <template #ITEM="{ option }">
              <img class="acc-item-icon" :src="`/sh_static/accounts/${option.uniqueName}.png`" />
              <div class="acc-item-name">
                {{ option.name }}
              </div>
            </template>
          </vue-opti-select-light>
        </div>

        <!-- TrafficSource Accounts -->
        <div class="col-md-3">
          <vue-opti-select-light
            class="optimizer-select"
            :options="trafficSourceAccountOptions"
            unique-key="id"
            option-type="checkbox"
            :search-fields="['name']"
            button-placeholder="All Accounts"
            button-placeholder-all-selected="All Accounts"
            :button-placeholder-multiple="({ count, suffix }) => `${count} Account${suffix}`"
            searchable
            button-block
            v-model="models.trafficSourceAccount"
            @change="$_changeTrafficSourceAccount"
            :disabled="trafficSourceAccountOptions.length === 0"
          >
            <template #ITEM="{ option }">
              <img class="acc-item-icon" :src="`/sh_static/accounts/${option.trafficSourceType.uniqueName}.png`" />
              <div class="acc-item-name">
                {{ option.name }}
              </div>
            </template>
          </vue-opti-select-light>
        </div>

        <!-- Report Types -->
        <div class="col-md-3">
          <vue-opti-select-light
            ref="trafficSourceType"
            class="optimizer-select"
            :options="$c_reportTypeOptions"
            :value="models.reportType"
            @change="(option) => { models.reportType = option.value }"
            button-placeholder="Select Report Type"
            button-block
            single
          />
        </div>

        <!-- Report DateRange -->
        <div class="col-md-3">
          <date-range
            auto-emit
            v-model="models.reportDateRange"
          />
        </div>
      </div>
    </div>

    <div class="reporting-filter-wrapper__campaigns">
      <div class="row mb-3">
        <!-- Campaigns -->
        <div class="col-md-12">
          <div class="w-100 mb-1">
            <div class="row">
              <b-button-group size="sm" class="col-auto campaign-buttons">
                <b-btn variant="outline-secondary" @click="$_campaignsFilter('all')" class="filter-btn">All</b-btn>
                <b-btn variant="outline-secondary" @click="$_campaignsFilter('active')" class="filter-btn">Only Active</b-btn>
                <b-btn variant="outline-secondary" @click="$_campaignsFilter('disabled')" class="filter-btn">Only Disabled</b-btn>
                <b-btn variant="outline-secondary" @click="$_campaignsFilter('none')" class="filter-btn">None</b-btn>
                <b-dropdown variant="outline-secondary" text="Search" toggle-class="filter-btn" @hidden="$_dropdownHidden">
                  <div class="searchCampaignItem">
                    <b-input v-model="searchModel" placeholder="Search..." autocomplete="off" class="searchCampaignInput w-100" />
                    <div class="searchInfo d-flex justify-content-between">
                      <span class="count-display">Found: {{ $c_searchResult.length }}</span>
                      <b-btn class="add-btn secondary-button" size="sm" @click="$_addSearchCampaigns">Add</b-btn>
                    </div>
                  </div>
                </b-dropdown>
              </b-button-group>
            </div>
          </div>
          <div class="w-100 ">
            <campaign-select
              v-model="models.campaigns"
              :options="$c_campaignsOptions"
              :enable-filter="false"
            />
          </div>
          <div class="campaigns-text-info">{{ models.campaigns.length }} campaigns selected</div>
        </div>
      </div>
    </div>

    <div class="row reporting-filter-wrapper__actions">
      <!-- Download button -->
      <div v-if="download" class="col-sm-6 col-md-3">
        <download-excel
          :name="`${models.trafficSourceType} - ${models.reportType} reporting.xls`"
          class="btn btn-default btn-block download-btn"
          :disabled="!$c_isCompleted"
          :fetch="$_downloadReport"
          :fields="downloadFields"
          :before-generate="() => isDownloading = true"
          :before-finish="() => isDownloading = false"
        >
          <i class="fa fa-download"></i>
          Download
        </download-excel>
      </div>

      <!-- Submit button -->
      <div class="col-sm-6 col-md-3">
        <b-btn class="btn-block primary-button" @click="$_submit" v-if="!download" :disabled="!$c_isCompleted" :variant="$c_isCompleted ? 'primary' : 'outline-primary'">
          <i class="fa fa-spinner fa-spin" v-if="isSubmiting"></i>
          <i class="fa fa-filter" v-else></i>
          Submit
        </b-btn>
      </div>
    </div>

    <b-modal
      ref="reportDownloadingModal"
      v-model="isDownloading"
      no-close-on-backdrop
      no-close-on-esc
      hide-header
      hide-footer
      body-bg-variant="light"
      centered
    >
      <template v-if="models">
        <h5 class="text-center">Downloading report <i class="fa fa-spinner fa-spin"></i></h5>
        <p class="mb-0" style="font-size: 16px;">
          <img style="width: 45px; display: inline; margin-right: 3px;" :src="`/sh_static/accounts/${models.trafficSourceType}.png`" />
          <b>Preparing report for {{ models.trafficSourceType }} {{ models.reportType }}</b>
        </p>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
import notifications from '@sh/mixins/notifications';
import configFile from './config';

const FORMAT = 'YYYY-MM-DD';

const defaultRange = {
  startDate: moment(new Date()).subtract(6, 'days').startOf('day').format(FORMAT),
  endDate: moment(new Date()).endOf('day').format(FORMAT),
};
export default {
  name: 'ReportingFilter',
  mixins: [notifications],
  props: {
    submiting: { type: Boolean, required: true },
  },
  data() {
    return {
      config: null,
      accounts: [],
      campaigns: [],
      models: {
        trafficSourceType: null,
        trafficSourceAccount: [],
        reportType: null,
        reportDateRange: null,
        campaigns: [],
      },
      searchModel: '',
      trafficSourceAccountOptions: [], // Need to replace with vue-opti-multiselect
      download: false,
      isDownloading: false,
      isSubmiting: false,
      isSubmittingReport: false,
      downloadFields: {},
    };
  },
  computed: {
    $c_trafficSourceTypeOptions() {
      const options = [];
      const optionsObj = {};
      this.accounts.forEach((item) => {
        if (!optionsObj[item.type.uniqueName]) {
          optionsObj[item.type.uniqueName] = item.type;
        }
      });
      for (const uniqueName in optionsObj) {
        options.push(optionsObj[uniqueName]);
      }
      return options;
    },
    $c_selectedTrafficSourceAccountsId() {
      return this.models.trafficSourceAccount.length ? this.models.trafficSourceAccount.map(({ id }) => id) : this.trafficSourceAccountOptions.map(({ id }) => id);
    },
    $c_reportTypeOptions() {
      const { options } = this.config.model.getReportOptions(this.models.trafficSourceType);
      return options.map((option) => ({
        value: option.reportType.toLowerCase(),
        content: option.name || option.reportType,
      }));
    },
    $c_campaignsOptions() {
      if (this.campaigns.length === 0 || this.models.trafficSourceType === null) return [];
      return this.campaigns.filter((item) => (item.traffic_source_unique_name === this.models.trafficSourceType && this.$c_selectedTrafficSourceAccountsId.indexOf(item.traffic_source_account_id) > -1));
    },
    $c_isCompleted() {
      return this.models.trafficSourceType && this.$c_selectedTrafficSourceAccountsId.length > 0 && this.models.reportType
      && this.models.reportDateRange.startDate && this.models.reportDateRange.endDate && this.models.campaigns.length > 0;
    },
    $c_searchResult() {
      const formatedSearch = this.searchModel.toLowerCase();
      if (formatedSearch.trim() === '') return [];
      return this.$c_campaignsOptions.filter((item) => item.name.toLowerCase().indexOf(formatedSearch) > -1);
    },
  },
  watch: {
    submiting(value) {
      this.isSubmiting = value;
    },
  },
  async created() {
    this.config = await configFile.getConfig();
    const dontShowTrafficSources = ['TikTok', 'GoogleAds', 'YahooDSP', 'Facebook', 'VoluumDSP', 'ContentAd'];
    try {
      [this.accounts, this.campaigns] = await Promise.all([this.config.api.trafficSources.accounts(), this.config.api.campaigns.all()]);
      this.accounts = this.accounts.filter((item) => !dontShowTrafficSources.includes(item.type.uniqueName));
      this.campaigns = this.campaigns.map((item) => {
        item.value = item.id;
        item.uniqueName = item.traffic_source_unique_name;
        return item;
      });
      this.$emit('ready');
    } catch (error) {
      this.$n_failNotification({ title: error.response.message || 'An error occurred' });
    }
    if (!this.$route.query.startDate || !this.$route.query.endDate) {
      this.$router.replace({ query: { ...this.$route.query, startDate: defaultRange.startDate, endDate: defaultRange.endDate } });
    }
  },
  methods: {
    $_changeTrafficSourceType(option) {
      this.models.trafficSourceType = option.uniqueName;
      this.models.trafficSourceAccount = [];
      this.trafficSourceAccountOptions = this.accounts.filter((account) => account.type.uniqueName === option.uniqueName);
      this.models.reportType = null;
      this.models.campaigns = [];
    },
    $_changeTrafficSourceAccount() {
      this.models.reportType = null;
      this.models.campaigns = [];
    },
    $_campaignsFilter(action) {
      // eslint-disable-next-line no-fallthrough
      if (action === 'all') {
        this.models.campaigns = this.$c_campaignsOptions;
      } else if (action === 'active') {
        this.models.campaigns = this.$c_campaignsOptions.filter((item) => item.enabled);
      } else if (action === 'disabled') {
        this.models.campaigns = this.$c_campaignsOptions.filter((item) => !item.enabled);
      } else if (action === 'none') {
        this.models.campaigns = [];
      }
      // }
    },
    $_submit() {
      const submitData = {
        trafficSourceType: this.models.trafficSourceType,
        trafficSourceAccountsId: this.$c_selectedTrafficSourceAccountsId,
        reportType: this.config.model.types[this.models.reportType],
        dateRange: { ...this.models.reportDateRange },
        campaignsId: this.models.campaigns.map((item) => item.campaign_id),
      };
      this.$emit('filter', submitData, 'submit');
    },
    $_backgroundReporting() {
      const submitData = {
        reportType: 'backgroundReporting',
      };
      this.$emit('filter', submitData, 'savedReports');
    },
    async $_downloadReport() {
      const { reportType } = this.config.model.types[this.models.reportType];
      try {
        this.downloadFields = {};
        const { fields, items } = await this.config.api.reporting.get(this.models.trafficSourceType, reportType, this.models.campaigns.map((item) => item._id), this.models.reportDateRange.startDate, this.models.reportDateRange.endDate);
        fields.forEach((field) => {
          this.downloadFields[field.header.content] = field.item.key;
        });
        return items;
      } catch (error) {
        this.$n_failNotification({ title: error || 'An error occurred' });
        return [];
      }
    },
    $_addSearchCampaigns() {
      const modelMap = {};
      this.models.campaigns.forEach((item) => { modelMap[item.id] = item; });
      this.$c_searchResult.forEach((item) => {
        if (!modelMap[item.id]) this.models.campaigns.push(item);
      });
    },
    $_dropdownHidden() {
      this.searchModel = '';
    },
  },
};
</script>

<style lang="scss">
.reporting-filter-wrapper {
  padding: 2.5rem;

  &__filters {
    margin-bottom: 3rem;
  }

  &__campaigns {
    .campaigns-text-info {
      font-size: 1.4rem;
      font-style: italic;
      color: $black-400;
      margin-top: .5rem;
    }

    .campaign-buttons {
      .filter-btn {
        border: none;
        margin-right: 1.5rem;
        color: #459ef1 !important;
        font-size: 1.4rem;
        cursor: pointer;

        &:hover {
          background: transparent;
          text-decoration: underline;
        }
      }

      .dropdown {
        &.show {
          .dropdown-toggle {
            background: transparent;
          }
        }

        .dropdown-menu {
          padding: 1.5rem;
          border: none;
          border-radius: .6rem;
          box-shadow: $bs-lg;

          .searchCampaignItem {
            input {
              min-width: 15rem;
              margin-bottom: 1rem;
              height: 3.2rem;
              font-size: 1.3rem;
              border-radius: .4rem;
            }

            .searchInfo {
              align-items: center;

              .count-display {
                font-size: 1.5rem;
              }

              .add-btn {
                min-width: unset;
              }
            }
          }
        }
      }
    }
  }

  &__actions {
    justify-content: flex-end;

    .primary-button {
      &[disabled] {
        background: cornflowerblue !important;
        border-color: cornflowerblue !important;
        color: $color-white;
        cursor: not-allowed;
      }
    }
  }

  .vs__selected-options {
    max-height: 20rem;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .submit-btn {
    &[disabled] {
      cursor: not-allowed;
    }
  }
  .download-btn {
    background: #20a8d8;
    border-color: #20a8d8;
    color: white;
    cursor: pointer;
    &[disabled] {
      cursor: not-allowed;
      opacity: 0.6;
      background: none;
      color: #20a8d8;
    }
  }
  .campaignCloneModal {
    .modal-body {
      border: 2px solid #20a8d8;
    }
  }
}
</style>
